/* ============================================
   PullToRefresh — Native-feel pull-to-refresh
   Wraps content and detects pull-down gesture
   to trigger a page reload, like native apps.
   ============================================ */
import { useState, useRef, useCallback, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  threshold?: number // px to pull before triggering refresh (default 80)
}

export default function PullToRefresh({ children, threshold = 80 }: Props) {
  const [pullDistance, setPullDistance] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const startY = useRef(0)
  const pulling = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const canPull = useCallback((): boolean => {
    // Only allow pull when content is scrolled to top
    // Check all scrollable parents — if any are scrolled down, don't pull
    const el = containerRef.current
    if (!el) return false

    // Walk up from the touch target to find scrollable elements
    let node: HTMLElement | null = el
    while (node) {
      if (node.scrollTop > 0) return false
      node = node.parentElement
    }
    return true
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (refreshing) return
    startY.current = e.touches[0].clientY
    pulling.current = false
  }, [refreshing])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (refreshing) return
    const currentY = e.touches[0].clientY
    const diff = currentY - startY.current

    // Only pull down, not up
    if (diff <= 0) {
      if (pulling.current) {
        pulling.current = false
        setPullDistance(0)
      }
      return
    }

    // Check if we can pull (content at top)
    if (!pulling.current) {
      if (!canPull()) return
      pulling.current = true
      startY.current = currentY // Reset start to current position
      return
    }

    // Apply resistance: the further you pull, the harder it gets
    const resistance = Math.min(diff * 0.45, threshold * 1.5)
    setPullDistance(resistance)
  }, [refreshing, canPull, threshold])

  const handleTouchEnd = useCallback(() => {
    if (!pulling.current || refreshing) {
      setPullDistance(0)
      return
    }

    if (pullDistance >= threshold) {
      // Trigger refresh
      setRefreshing(true)
      setPullDistance(threshold * 0.6) // Snap to loading position
      // Small delay for visual feedback, then reload
      setTimeout(() => window.location.reload(), 300)
    } else {
      // Snap back
      setPullDistance(0)
    }
    pulling.current = false
  }, [pullDistance, threshold, refreshing])

  const progress = Math.min(pullDistance / threshold, 1)

  return (
    <div
      ref={containerRef}
      className="ptr-container"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className="ptr-indicator"
        style={{
          transform: `translateY(${pullDistance - 44}px)`,
          opacity: refreshing ? 1 : progress,
        }}
      >
        <div
          className={`ptr-spinner${refreshing ? ' ptr-spinner--active' : ''}`}
          style={{
            transform: refreshing ? undefined : `rotate(${progress * 270}deg)`,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </div>
      </div>

      {/* Content with transform */}
      <div
        className="ptr-content"
        style={{
          transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
          transition: pulling.current ? 'none' : 'transform 0.3s ease',
        }}
      >
        {children}
      </div>
    </div>
  )
}
